import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { MapPin, TrendingUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * HomeCoverageHeatmap — grid of property tiles colored by platform coverage %.
 * Click any tile to jump into that property's Stack Map via the parent's onJumpProperty.
 * Reads from /api/analytics/property-coverage which the platform already exposes.
 */
export default function HomeCoverageHeatmap({ token, onJumpProperty }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${BACKEND}/api/reports/coverage-matrix`, { headers })
      .then(r => {
        const props = r.data.properties || [];
        const computed = props.map(p => {
          const cats = p.categories || {};
          const active = Object.values(cats).reduce((s, c) => s + (c.used || 0), 0);
          const total = Object.values(cats).reduce((s, c) => s + (c.total || 0), 0);
          const pct = total > 0 ? Math.round((active / total) * 100) : 0;
          // Compact abbreviation: initials of the first 3 words, or the first 4 chars
          const words = (p.property_name || '').split(/\s+/).filter(Boolean);
          let abbr;
          if (words.length >= 2) {
            abbr = words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
          } else {
            abbr = (p.property_name || '').slice(0, 4).toUpperCase();
          }
          return { id: p.property_id, name: p.property_name, abbr, active, total, pct };
        }).sort((a, b) => b.pct - a.pct);
        setRows(computed);
      })
      .catch(() => { setRows([]); })
      .finally(() => setLoading(false));
  }, [token, headers]);

  const colorFor = (pct) => {
    // green > amber > red gradient with soft opacity
    if (pct >= 30) return { bg: 'bg-emerald-500', fg: 'text-white', label: 'strong' };
    if (pct >= 20) return { bg: 'bg-emerald-400', fg: 'text-white', label: 'good' };
    if (pct >= 15) return { bg: 'bg-amber-400', fg: 'text-amber-950', label: 'ok' };
    if (pct >= 10) return { bg: 'bg-amber-300', fg: 'text-amber-950', label: 'gaps' };
    if (pct >= 5) return { bg: 'bg-rose-300', fg: 'text-rose-950', label: 'low' };
    return { bg: 'bg-slate-200 dark:bg-slate-800', fg: 'text-slate-600 dark:text-slate-400', label: 'empty' };
  };

  if (loading) {
    return (
      <div className="border rounded-xl p-4 bg-card/50">
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-40 bg-muted rounded" />
          <div className="grid grid-cols-8 gap-1.5 mt-3">
            {Array.from({ length: 24 }).map((_, i) => <div key={i} className="aspect-square rounded-md bg-muted" />)}
          </div>
        </div>
      </div>
    );
  }

  if (rows.length === 0) return null;

  const avg = rows.length ? Math.round(rows.reduce((s, r) => s + r.pct, 0) / rows.length) : 0;

  return (
    <div className="border rounded-xl p-4 bg-card/50" data-testid="coverage-heatmap">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <MapPin className="h-3 w-3" /> Coverage Heatmap
          <span className="text-[10px] normal-case text-muted-foreground font-normal">({rows.length} properties)</span>
        </h3>
        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> Portfolio avg: <span className="font-bold text-foreground">{avg}%</span>
        </div>
      </div>

      <TooltipProvider delayDuration={200}>
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-1.5" data-testid="heatmap-grid">
          {rows.map(r => {
            const c = colorFor(r.pct);
            return (
              <Tooltip key={r.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onJumpProperty && onJumpProperty(r.id)}
                    className={`group relative aspect-square rounded-md ${c.bg} ${c.fg} flex flex-col items-center justify-center px-1 hover:ring-2 hover:ring-primary hover:ring-offset-1 hover:ring-offset-background transition-all shadow-sm`}
                    data-testid={`heatmap-tile-${r.id}`}
                  >
                    <span className="text-[13px] font-bold leading-none">{r.pct}<span className="text-[8px] opacity-70">%</span></span>
                    <span className="text-[8px] font-semibold opacity-80 leading-none mt-1 truncate max-w-full" title={r.name}>{r.abbr}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-[10px] opacity-80">{r.active} / {r.total} platforms · {c.label}</div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Legend */}
      <div className="mt-3 flex items-center gap-2 flex-wrap text-[9px] text-muted-foreground">
        <span>Legend:</span>
        {[
          { label: '<5%', bg: 'bg-slate-200 dark:bg-slate-800' },
          { label: '5-10%', bg: 'bg-rose-300' },
          { label: '10-15%', bg: 'bg-amber-300' },
          { label: '15-20%', bg: 'bg-amber-400' },
          { label: '20-30%', bg: 'bg-emerald-400' },
          { label: '>30%', bg: 'bg-emerald-500' },
        ].map(l => (
          <span key={l.label} className="flex items-center gap-1">
            <span className={`h-2 w-2 rounded ${l.bg}`} /> {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}
